import { ApiError } from "@/api/errors";
import { currentOwnerKey, getToken } from "@/api/session";
import { dispatchItem } from "./handlers";
import { isSessionDeferred } from "./sessionGate";
import type { OutboxStore } from "./store";
import type { OutboxFailure, OutboxItem } from "./types";

type DispatchVerdict = "sent" | "retry" | "failed" | "waits-for-session";

interface DispatcherOptions {
  store: OutboxStore;
  dispatch?: (item: OutboxItem) => Promise<void>;
  now?: () => Date;
  isOnline?: () => boolean;
  hasSession?: () => boolean;
  baseDelayMs?: number;
  maxDelayMs?: number;
  maxAttempts?: number;
  wakeIntervalMs?: number;
}

const DEFAULT_BASE_DELAY_MS = 5_000;
const DEFAULT_MAX_DELAY_MS = 5 * 60_000;
const DEFAULT_MAX_ATTEMPTS = 8;
const DEFAULT_WAKE_INTERVAL_MS = 30_000;

function backoffDelayMs(attempts: number, baseMs: number, maxMs: number): number {
  return Math.min(maxMs, baseMs * 2 ** Math.max(0, attempts));
}

function verdictFor(error: unknown): DispatchVerdict {
  const status = error instanceof ApiError ? error.status : undefined;
  if (status === undefined) {
    return "retry";
  }
  if (status === 401) {
    return "waits-for-session";
  }
  if (status >= 400 && status < 500) {
    return "failed";
  }
  return "retry";
}

function failureOf(error: unknown, at: Date, retriable: boolean, fallback: string): OutboxFailure {
  const apiError = error instanceof ApiError ? error : null;
  return {
    message: apiError?.message || fallback,
    status: apiError?.status ?? null,
    code: apiError?.code ?? null,
    failedAt: at.toISOString(),
    retriable,
  };
}

export class OutboxDispatcher {
  private readonly store: OutboxStore;

  private readonly dispatch: (item: OutboxItem) => Promise<void>;

  private readonly now: () => Date;

  private readonly isOnline: () => boolean;

  private readonly hasSession: () => boolean;

  private readonly baseDelayMs: number;

  private readonly maxDelayMs: number;

  private readonly maxAttempts: number;

  private readonly wakeIntervalMs: number;

  private running: Promise<void> | null = null;

  private timer: ReturnType<typeof setInterval> | null = null;

  private readonly wake = () => {
    void this.run();
  };

  constructor(options: DispatcherOptions) {
    this.store = options.store;
    this.dispatch = options.dispatch ?? dispatchItem;
    this.now = options.now ?? (() => new Date());
    this.isOnline = options.isOnline ?? (() => navigator.onLine);
    this.hasSession = options.hasSession ?? (() => this.servesCurrentSession());
    this.baseDelayMs = options.baseDelayMs ?? DEFAULT_BASE_DELAY_MS;
    this.maxDelayMs = options.maxDelayMs ?? DEFAULT_MAX_DELAY_MS;
    this.maxAttempts = options.maxAttempts ?? DEFAULT_MAX_ATTEMPTS;
    this.wakeIntervalMs = options.wakeIntervalMs ?? DEFAULT_WAKE_INTERVAL_MS;
  }

  start(): void {
    if (this.timer !== null) {
      return;
    }
    window.addEventListener("online", this.wake);
    this.timer = setInterval(this.wake, this.wakeIntervalMs);
    this.wake();
  }

  stop(): void {
    if (this.timer === null) {
      return;
    }
    window.removeEventListener("online", this.wake);
    clearInterval(this.timer);
    this.timer = null;
  }

  private servesCurrentSession(): boolean {
    if (getToken() === null || isSessionDeferred()) {
      return false;
    }
    const claimed = this.store.currentOwnerKey();
    return claimed !== null && claimed === currentOwnerKey();
  }

  run(): Promise<void> {
    if (this.running) {
      return this.running;
    }
    this.running = this.drain().finally(() => {
      this.running = null;
    });
    return this.running;
  }

  private async drain(): Promise<void> {
    if (!this.isOnline() || !this.hasSession()) {
      return;
    }
    for (const item of this.store.getSnapshot().items) {
      if (!this.isOnline() || !this.hasSession()) {
        return;
      }
      if (item.status !== "pending") {
        continue;
      }
      if (Date.parse(item.nextAttemptAt) > this.now().getTime()) {
        continue;
      }
      const verdict = await this.send(item);
      if (verdict === "waits-for-session") {
        return;
      }
    }
  }

  private async send(item: OutboxItem): Promise<DispatchVerdict> {
    try {
      await this.dispatch(item);
      await this.store.remove(item.id);
      return "sent";
    } catch (error) {
      return this.settle(item, error);
    }
  }

  private async settle(item: OutboxItem, error: unknown): Promise<DispatchVerdict> {
    const verdict = verdictFor(error);
    const at = this.now();
    if (verdict === "waits-for-session") {
      return verdict;
    }
    if (verdict === "retry") {
      const attempts = item.attempts + 1;
      if (attempts >= this.maxAttempts) {
        await this.store.markFailed(
          item.id,
          failureOf(error, at, true, "No se pudo enviar después de varios intentos"),
        );
        return "failed";
      }
      const delay = backoffDelayMs(attempts, this.baseDelayMs, this.maxDelayMs);
      await this.store.scheduleRetry(
        item.id,
        new Date(at.getTime() + delay),
        failureOf(error, at, true, "Sin conexión con el servidor"),
      );
      return verdict;
    }
    await this.store.markFailed(
      item.id,
      failureOf(error, at, false, "El servidor rechazó el envío"),
    );
    return verdict;
  }
}
